import React, { useCallback, useMemo } from 'react';
import { TableCell, Theme, Tooltip, Typography } from '@mui/material';
import { ProjectRowProps } from '../types';
import { Link } from 'react-router-dom';
import BreadcrumbLink from '@mui/material/Link';
import { ProjectTableRow } from './styledComponents/ProjectTableRow';
import { useAppSelector } from '../../../redux/hooks';
import { getGroupBy, GroupAccountMembersBy } from '../../../redux/account';
import {
  GrossMarginHighlightColor,
  NetMarginHighlightColor,
} from '../../../constants/marginTracker';
import { MARGINS_NAME_COLUMN_WIDTH } from '../marginsTableLayout';

export const ProjectRow = ({
  members,
  name,
  projectId,
  grossMargin,
  netMargin,
  showMargin,
  hideLink = false,
  grossMarginHighlights,
  netMarginHighlights,
}: ProjectRowProps) => {
  const isProjectView = useMemo(() => Boolean(projectId), [projectId]);
  const groupBy = useAppSelector(getGroupBy);

  const columnWidth = (theme: Theme) => theme.spacing(10);

  const themeColor = useCallback(
    (value: number | null | undefined, category: string) => {
      const marginType =
        category === 'gross'
          ? grossMarginHighlights.find(
              (marginHighlight) =>
                value != null &&
                (marginHighlight.min ?? -Infinity) <= value &&
                (marginHighlight.max ?? Infinity) >= value,
            )?.type
          : netMarginHighlights.find(
              (marginHighlight) =>
                value != null &&
                (marginHighlight.min ?? -Infinity) <= value &&
                (marginHighlight.max ?? Infinity) >= value,
            )?.type;
      return category === 'gross'
        ? GrossMarginHighlightColor[marginType || ''] ?? 'text.primary'
        : NetMarginHighlightColor[marginType || ''] ?? 'text.primary';
    },
    [grossMarginHighlights, netMarginHighlights],
  );

  const nameContent = (
    <>
      <Typography variant="h4" component="p">
        {name}
      </Typography>
      <Typography variant="subtitle2" color="text.secondary">
        {members}
      </Typography>
    </>
  );

  const nameCellContent =
    groupBy === GroupAccountMembersBy.Team && !hideLink ? (
      <BreadcrumbLink
        component={Link}
        to={`../${projectId}` || ''}
        className="projectRow no-height"
        underline="hover"
        sx={{
          color: 'text.secondary',
          textDecoration: 'none',
          '&:hover': {
            color: (theme) => theme.palette.primary.main,
          },
        }}
      >
        {nameContent}
      </BreadcrumbLink>
    ) : (
      nameContent
    );

  const netMarginHelperText = useMemo(() => {
    if (!netMargin) return '';
    if (netMargin >= 20) return '20% and above - Approved margin, best case.';
    if (netMargin >= 15) return '15 .. 19.99% - Approved margin, decent case.';
    if (netMargin >= 10)
      return '10 .. 14.99% - Approved margin, minimum margin.';
    if (netMargin >= 5) return '5 .. 9.99% - Approval required, low margin.';
    return '4.99% and below - No go, negative margin.';
  }, [netMargin]);

  const grossMarginHelperText = useMemo(() => {
    if (!grossMargin) return '';
    if (grossMargin >= 40) return '40% and above - on target.';
    if (grossMargin >= 37) return '37 .. 39.99% - yellow.';
    if (grossMargin >= 34) return '34 .. 36.99% - red.';
    if (grossMargin >= 32) return '32 .. 33.99% - panic.';
    return "31.99% and below - shouldn' happen.";
  }, [grossMargin]);

  return (
    <ProjectTableRow
      isProjectView={isProjectView}
      className="projectRow no-height"
      sx={{ width: '100%' }}
    >
      <TableCell
        sx={{
          border: '0',
          py: 1,
          maxWidth: MARGINS_NAME_COLUMN_WIDTH,
          minWidth: MARGINS_NAME_COLUMN_WIDTH,
          width: MARGINS_NAME_COLUMN_WIDTH,
        }}
        colSpan={1}
      >
        {nameCellContent}
      </TableCell>
      <TableCell
        sx={{
          border: '0',
          py: 1,
          textAlign: 'center',
          width: columnWidth,
        }}
        colSpan={1}
      >
        {showMargin && (
          <Tooltip title={grossMarginHelperText} placement="top">
            <Typography
              variant="h4"
              component="p"
              sx={{
                backgroundColor: themeColor(grossMargin, 'gross'),
                color: 'white',
                borderRadius: 15,
                py: 0.5,
                px: 1,
              }}
            >
              {grossMargin === undefined || grossMargin === null
                ? 'NA'
                : `${grossMargin}%`}
            </Typography>
          </Tooltip>
        )}
      </TableCell>
      <TableCell
        sx={{
          border: '0',
          textAlign: 'center',
          width: columnWidth,
        }}
        colSpan={1}
      >
        {showMargin && (
          <Tooltip title={netMarginHelperText} placement="top">
            <Typography
              variant="h4"
              component="p"
              sx={{
                backgroundColor: themeColor(netMargin, 'net'),
                color: 'white',
                borderRadius: 15,
                py: 0.5,
                px: 1,
              }}
            >
              {netMargin === undefined || netMargin === null
                ? 'NA'
                : `${netMargin}%`}
            </Typography>
          </Tooltip>
        )}
      </TableCell>
      <TableCell
        colSpan={6}
        sx={{
          border: '0',
          py: 1,
        }}
      ></TableCell>
    </ProjectTableRow>
  );
};
